// @ts-strict-ignore
/**
 * Precedence Indexed actions are actions that have a precedence index.
 * Currently, only Outcomes and Workflow Routing actions have a precedence
 * index. There are many different functions that work with these values,
 * and their logic is basically the same, so we centralize those functions
 * in this file instead of having repeated functions for each.
 *
 * Once we support separate types for each action, we can look into using interfaces
 * to implement this contract on each action type that needs it.
 */

import type { WorkflowState } from 'flow/store/redux/state';
import type { Action } from 'flow/store/engine/types';
import { ActionType } from '../../../store/engine/constants';
import { WorkflowEngineException } from '../../../store/engine/schema';
import { ReduxErrorTypes } from '../../../store/redux/constants';
import { actionHasRules, getActionDataObjectKeyName, isActionOfType } from '../actions';
import { DEFAULT_OUTCOME_PRECEDENCE_INDEX } from '../outcomes';
import { DEFAULT_WORKFLOW_ROUTING_PRECEDENCE_INDEX } from '../workflowRouting';


/**
 * Precedence-Indexed actions are actions that are ordered by a certain precedence index
 * like outcomes and workflow routing actions are. Both types of actions share most of their
 * abstractions, so we treat them as the same thing.
 */
export function isPrecedenceIndexedAction(action: Action) {
  return isActionOfType(action, [ActionType.OUTCOME, ActionType.WORKFLOW]);
}

export function validatePrecedenceIndexedAction(action: Action) {
  if (!isPrecedenceIndexedAction(action)) {
    throw new WorkflowEngineException(
      `Action of type ${action.type} does not have a precedence index`,
      ReduxErrorTypes.INVALID_ACTION_TYPE,
    );
  }
}

export function getPrecedenceIndexedDataKey(action: Action) {
  const dataKey = getActionDataObjectKeyName(action);

  /**
   * our version of TS forced us to run a manual check to help it infer that data key
   * is only `outcome` or `workflow`
   */
  if (dataKey !== 'outcome' && dataKey !== 'workflow') {
    throw new WorkflowEngineException(
      `Action of type ${action.type} does not have a precedence index`,
      ReduxErrorTypes.INVALID_ACTION_TYPE,
    );
  }

  return dataKey;
}

export function isDefaultPrecedenceIndexedAction(action: Action) {
  if (!action) {
    return false;
  }


  if (!isPrecedenceIndexedAction(action)) {
    return false;
  }


  const hasNoRules = !actionHasRules(action);
  const hasDefaultPrecedenceIndex = isActionOfType(action, ActionType.OUTCOME)
    ? action.outcome.precedenceIndex === DEFAULT_OUTCOME_PRECEDENCE_INDEX
    : action.workflow.precedenceIndex === DEFAULT_WORKFLOW_ROUTING_PRECEDENCE_INDEX;

  return hasNoRules && hasDefaultPrecedenceIndex;
}


export function isActionOfHighestPrecedence(
  actions: WorkflowState['workflowData']['actions'],
  action: Action,
  actionType: ActionType.OUTCOME | ActionType.WORKFLOW,
) {
  if (!action) {
    return false;
  }
  validatePrecedenceIndexedAction(action);

  const sortedOutcomeActionsByPrecedenceDESC = getSortedActionsByPrecedence(
    actions,
    actionType,
    'DESC',
  );

  return sortedOutcomeActionsByPrecedenceDESC.at(0)?.publicId === action.publicId;
}

export function sortPrecedenceASC(a: Action, b: Action) {
  const dataKey = getPrecedenceIndexedDataKey(a);
  return a[dataKey].precedenceIndex - b[dataKey].precedenceIndex;
}


export function sortPrecedenceDESC(a: Action, b: Action) {
  const dataKey = getPrecedenceIndexedDataKey(a);
  return b[dataKey].precedenceIndex - a[dataKey].precedenceIndex;
}

export function getSortedActionsByPrecedence(
  actions: WorkflowState['workflowData']['actions'],
  actionType: ActionType.OUTCOME | ActionType.WORKFLOW,
  order: 'ASC' | 'DESC' = 'ASC',
) {
  if (!actions) {
    return [];
  }

  const sortingFn = order === 'ASC' ? sortPrecedenceASC : sortPrecedenceDESC;

  return Object
    .values(actions)
    .filter(a => isActionOfType(a, actionType))
    .sort(sortingFn);
}


/**
 * Finds the next action in terms of precedence
 *
 * @param originalAction action to boost precedence
 * @param actionsOfType all actions of the originalAction type in the workflow
 * @returns the next action in terms of precedence
 *
 */
export function findNextActionBasedOnPrecedence(
  originalAction: Action,
  actionsOfType: Action[],
): Action {
  validatePrecedenceIndexedAction(originalAction);
  const dataKey = getPrecedenceIndexedDataKey(originalAction);

  const originalPrecedence = originalAction[dataKey].precedenceIndex;

  const actionsWithoutOriginalSortedByPrecedenceASC = actionsOfType
    .filter(a => a.publicId !== originalAction.publicId)
    .sort(sortPrecedenceASC);

  const actionToSwapPrecedenceWith = actionsWithoutOriginalSortedByPrecedenceASC
    .find((action, idx, array) => {
      const currentAction = action;
      const previousAction = array[idx - 1];

      if (!previousAction) {
        return false;
      }

      if (originalPrecedence < currentAction[dataKey].precedenceIndex
        && originalPrecedence > previousAction[dataKey].precedenceIndex) {
        return true;
      }

      return false;
    });

  return actionToSwapPrecedenceWith;
}


export function findHighestExistingActionPrecedence(
  precedenceIndexedActions: Action[],
): number {
  if (precedenceIndexedActions.length === 0) {
    return 0;
  }
  // it's assumed that all actions passed are of the same type
  const dataKey = getPrecedenceIndexedDataKey(precedenceIndexedActions[0]);
  return Math.max(...precedenceIndexedActions.map(action => action[dataKey].precedenceIndex));
}


/**
 * finds an action `offset` number of actions away from the highest precedence one.
 * For example, `offset = 2`, the third highest precedence action is returned
 */
export function findActionFromHighestPrecedence(
  precedenceIndexedActions: Action[],
  offset: number,
) {
  if (offset < 0) {
    throw new RangeError('Cannot find action using negative index');
  }

  if (offset > precedenceIndexedActions.length) {
    throw new RangeError('Cannot find action with offset larger than actions length');
  }

  const sortedActionsASC = [...precedenceIndexedActions].sort(sortPrecedenceASC);
  return sortedActionsASC.at(-(offset + 1));
}


export function findNextActionPrecedence(
  precedenceIndexedActions: Action[],
): number {
  return findHighestExistingActionPrecedence(precedenceIndexedActions) + 1;
}


export function isMidPrecedenceAction(
  action: Action,
  precedenceActions: Action[],
) {
  validatePrecedenceIndexedAction(action);

  const highestPrecedenceIndex = findHighestExistingActionPrecedence(precedenceActions);

  const actionPrecedence = isActionOfType(action, ActionType.OUTCOME)
    ? action.outcome.precedenceIndex
    : action.workflow.precedenceIndex;

  const defaultActionPrecedence = isActionOfType(action, ActionType.OUTCOME)
    ? DEFAULT_OUTCOME_PRECEDENCE_INDEX
    : DEFAULT_WORKFLOW_ROUTING_PRECEDENCE_INDEX;


  return actionPrecedence !== defaultActionPrecedence
    && actionPrecedence !== highestPrecedenceIndex;
}


export function getActionPrecedenceIndex(action: Action): number | null {
  validatePrecedenceIndexedAction(action);

  if (isActionOfType(action, ActionType.OUTCOME)) {
    return action.outcome.precedenceIndex;
  }

  if (isActionOfType(action, ActionType.WORKFLOW)) {
    return action.workflow.precedenceIndex;
  }

  return null;
}

export function setActionPrecedenceIndex(action: Action, index: number): Action {
  validatePrecedenceIndexedAction(action);

  if (isActionOfType(action, ActionType.OUTCOME)) {
    return {
      ...action,
      outcome: {
        ...action.outcome,
        precedenceIndex: index,
      },
    };
  }


  if (isActionOfType(action, ActionType.WORKFLOW)) {
    return {
      ...action,
      workflow: {
        ...action.workflow,
        precedenceIndex: index,
      },
    };
  }

  return action;
}


export function getPrecedenceIndexedActions(actions: Record<Action['publicId'], Action>): Action[] {
  return Object
    .values(actions ?? {})
    .filter(isPrecedenceIndexedAction);
}

export function getNonPrecedenceIndexedActions(actions: Record<Action['publicId'], Action>): Action[] {
  return Object
    .values(actions ?? {})
    .filter(action => !isPrecedenceIndexedAction(action));
}

export function sortActionsByPrecedenceIndex(actions: Action[], order: 'ASC' | 'DESC' = 'ASC'): Action[] {
  return [...actions ?? []].sort((a, b) => {
    // do not sort a non-PI action
    if (!isPrecedenceIndexedAction(a) || !isPrecedenceIndexedAction(b)) {
      return 0;
    }

    if (order === 'ASC') {
      return getActionPrecedenceIndex(a) - getActionPrecedenceIndex(b);
    }

    return getActionPrecedenceIndex(b) - getActionPrecedenceIndex(a);
  });
}
